/**
 * 📊 Multi Campaign Details
 * Summary-Ansicht für Multi-Campaigns (ohne Slot-Liste)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { 
  ArrowLeft, Layers, Play, Pause, Clock, CheckCircle, XCircle, 
  AlertCircle, Activity,
  Eye, RefreshCw, Edit, Square, Copy, X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import MultiCampaignCreator from './MultiCampaignCreator';
import NdrBounceOverview from './NdrBounceOverview';

const MultiCampaignDetails = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFromSlot, setCopyFromSlot] = useState(1);
  const [rateLimit, setRateLimit] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);

  const startCampaignMutation = useMutation(
    async () => {
      const response = await axios.post(`/api/multi-campaigns/${campaignId}/start`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Campaign gestartet!');
        refetch();
      },
      onError: (error) => {
        toast.error('Fehler beim Starten der Campaign');
        console.error('Start campaign error:', error);
      }
    }
  );

  const stopCampaignMutation = useMutation(
    async () => {
      const response = await axios.post(`/api/multi-campaigns/${campaignId}/stop`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Campaign auf Draft zurückgesetzt');
        refetch();
      },
      onError: (error) => {
        toast.error('Fehler beim Zurücksetzen der Campaign');
        console.error('Stop campaign error:', error);
      }
    }
  );

  const { 
    data: campaignDetails, 
    isLoading, 
    refetch 
  } = useQuery(
    ['multiCampaignDetails', campaignId],
    async () => {
      const response = await axios.get(`/api/multi-campaigns/${campaignId}`);
      return response.data.data;
    },
    {
      refetchInterval: (data) => {
        if (!data) return false;
        if (data.status === 'running' || data.has_running_slots) return 10000;
        if (data.status === 'pending') return 15000;
        return false;
      },
      refetchIntervalInBackground: true
    }
  );

  const rateLimitMutation = useMutation(
    async (newRate) => {
      const response = await axios.post(`/api/multi-campaigns/${campaignId}/rate-limit`, { emails_per_hour: newRate });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(`Rate Limit auf ${data.emails_per_hour}/h gesetzt (${data.updated} Slots)`);
      },
      onError: () => toast.error('Fehler beim Ändern des Rate Limits')
    }
  );

  useEffect(() => {
    if (campaignDetails && rateLimit === null) {
      setRateLimit(campaignDetails.emails_per_hour || 3000);
    }
  }, [campaignDetails, rateLimit]);

  const activeSlot = campaignDetails?.active_slot || null;
  const isRunning = campaignDetails?.status === 'running' || campaignDetails?.has_running_slots;

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      draft: { color: 'gray', icon: Clock, text: 'Entwurf' },
      pending: { color: 'yellow', icon: Clock, text: 'Wartend' },
      running: { color: 'blue', icon: Activity, text: 'Läuft' },
      processing: { color: 'blue', icon: Activity, text: 'Verarbeitung' },
      paused: { color: 'orange', icon: Pause, text: 'Pausiert' },
      completed: { color: 'green', icon: CheckCircle, text: 'Abgeschlossen' },
      failed: { color: 'red', icon: XCircle, text: 'Fehlgeschlagen' },
      cancelled: { color: 'gray', icon: AlertCircle, text: 'Abgebrochen' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="h-4 w-4 mr-2" />
        {config.text}
      </span>
    );
  };

  const ProgressBar = ({ current, total, className = "" }) => {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    
    return (
      <div className={`w-full bg-gray-200 rounded-full h-3 ${className}`}>
        <div 
          className="bg-blue-600 h-3 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const handleCopyCampaign = async () => {
    setCopyLoading(true);
    try {
      const response = await axios.get(`/api/multi-campaigns/${campaignId}?slots=1`);
      const fullCampaign = response.data.data;
      const slots = fullCampaign.slots || [];

      const slotsToCopy = slots
        .filter(slot => slot.slot_number >= copyFromSlot)
        .map(slot => ({
          ...slot,
          slot_number: slot.slot_number - copyFromSlot + 1
        }));

      const copyCampaignData = {
        name: `${fullCampaign.name} (Kopie ab Slot ${copyFromSlot})`,
        description: fullCampaign.description,
        execution_mode: fullCampaign.execution_mode,
        campaign_config: fullCampaign.campaign_config || {},
        slots: slotsToCopy,
        central_recipients: fullCampaign.central_recipients || [],
        list_unsubscribe_enabled: fullCampaign.list_unsubscribe_enabled,
        list_unsubscribe_url: fullCampaign.list_unsubscribe_url,
        list_unsubscribe_method: fullCampaign.list_unsubscribe_method
      };

      sessionStorage.setItem('multiCampaignCopyData', JSON.stringify(copyCampaignData));
      toast.success(`${slotsToCopy.length} Slots kopiert!`);
      navigate('/jobs');
    } catch (error) {
      toast.error('Fehler beim Laden der Slots zum Kopieren');
      console.error('Copy campaign error:', error);
    } finally {
      setCopyLoading(false);
      setShowCopyModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!campaignDetails) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign nicht gefunden</h3>
        <button
          onClick={() => navigate('/jobs')}
          className="text-blue-600 hover:text-blue-700"
        >
          Zurück zur Übersicht
        </button>
      </div>
    );
  }

  const totalSent = campaignDetails.total_sent || 0;
  const totalFailed = campaignDetails.total_failed || 0;
  const totalRecipients = campaignDetails.total_recipients || 0;
  const totalSlots = campaignDetails.total_slots || 0;
  const successRate = totalSent + totalFailed > 0 
    ? Math.round((totalSent / (totalSent + totalFailed)) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/jobs')}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Layers className="h-6 w-6 mr-3 text-purple-600" />
                {campaignDetails.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-200">{campaignDetails.description}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <StatusBadge status={campaignDetails.status} />
            
            {campaignDetails.status === 'draft' && (
              <button
                onClick={() => startCampaignMutation.mutate()}
                disabled={startCampaignMutation.isLoading}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                <span>Campaign Starten</span>
              </button>
            )}
            
            {isRunning && (
              <button
                onClick={() => stopCampaignMutation.mutate()}
                disabled={stopCampaignMutation.isLoading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
              >
                <Square className="h-4 w-4" />
                <span>Laufende Slots Stoppen</span>
              </button>
            )}
            
            {(campaignDetails.status === 'completed' || campaignDetails.status === 'paused') && (
              <button
                onClick={() => setShowCopyModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
              >
                <Copy className="h-4 w-4" />
                <span>Kopieren ab Slot...</span>
              </button>
            )}
            
            {campaignDetails.status === 'draft' && totalSlots <= 500 && (
              <button
                onClick={() => setShowEditModal(true)}
                className="text-blue-600 hover:text-blue-700 flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Bearbeiten</span>
              </button>
            )}
            <button
              onClick={() => refetch()}
              className="text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{totalSlots}</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">Slots</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{totalRecipients}</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">Empfänger</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{totalSent}</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">Gesendet</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{totalFailed}</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">Fehlgeschlagen</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{successRate}%</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">Erfolgsrate</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-sm mb-2 text-gray-900 dark:text-gray-200">
            <span>Gesamt-Fortschritt:</span>
            <span>{totalSent + totalFailed}/{totalRecipients}</span>
          </div>
          <ProgressBar current={totalSent + totalFailed} total={totalRecipients} />
        </div>

        {campaignDetails.status !== 'draft' && (
          <NdrBounceOverview
            campaignId={campaignId}
            campaignStatus={campaignDetails.status}
          />
        )}

        {campaignDetails.status !== 'draft' && campaignDetails.status !== 'completed' && rateLimit !== null && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Rate Limit</span>
              <span className="text-lg font-bold text-blue-600">{rateLimit.toLocaleString()}/h</span>
            </div>
            <input
              type="range"
              min={500}
              max={10000}
              step={500}
              value={rateLimit}
              onChange={(e) => setRateLimit(parseInt(e.target.value))}
              onMouseUp={() => rateLimitMutation.mutate(rateLimit)}
              onTouchEnd={() => rateLimitMutation.mutate(rateLimit)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>500</span>
              <span>2.500</span>
              <span>5.000</span>
              <span>7.500</span>
              <span>10.000</span>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-200">
          <div>
            <span>Ausführungsmodus:</span>
            <span className="ml-2 font-medium">
              {campaignDetails.execution_mode === 'sequential' ? 'Sequenziell' : 'Parallel'}
            </span>
          </div>
          <div>
            <span>Erstellt:</span>
            <span className="ml-2 font-medium">
              {new Date(campaignDetails.created_at).toLocaleString('de-DE')}
            </span>
          </div>
          <div>
            <span>Campaign-ID:</span>
            <span className="ml-2 font-mono text-xs">{campaignDetails.campaign_id}</span>
          </div>
        </div>
      </div>

      {activeSlot && (
        <div className="bg-blue-50 dark:bg-dark-surface border border-blue-200 dark:border-dark-copper-dark rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 dark:text-gray-100 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Aktuell aktiver Slot #{activeSlot.slot_number}
          </h2>
          
          <div className="bg-white dark:bg-dark-secondary rounded-lg p-4 border dark:border-dark-copper-dark">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-200">{activeSlot.subject}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-200">Job-ID: {activeSlot.job_id}</p>
              </div>
              <StatusBadge status={activeSlot.job_status || activeSlot.status} />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">{activeSlot.recipient_count}</div>
                <div className="text-xs text-gray-500 dark:text-gray-300">Empfänger</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">{activeSlot.sent_count || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-300">Gesendet</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-red-600">{activeSlot.failed_count || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-300">Fehler</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-orange-600">{activeSlot.emails_per_hour}/h</div>
                <div className="text-xs text-gray-500 dark:text-gray-300">Rate</div>
              </div>
            </div>
            
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2 text-gray-900 dark:text-gray-200">
                <span>Slot-Fortschritt:</span>
                <span>{(activeSlot.sent_count || 0) + (activeSlot.failed_count || 0)}/{activeSlot.recipient_count}</span>
              </div>
              <ProgressBar 
                current={(activeSlot.sent_count || 0) + (activeSlot.failed_count || 0)} 
                total={activeSlot.recipient_count} 
              />
            </div>
            
            {activeSlot?.job_id && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => navigate(`/jobs/${activeSlot.job_id}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>Slot-Details anzeigen</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showEditModal && campaignDetails && (
        <MultiCampaignCreator
          editCampaign={campaignDetails}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
            toast.success('Multi-Campaign erfolgreich aktualisiert!');
          }}
        />
      )}

      {showCopyModal && campaignDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-secondary rounded-lg max-w-md w-full p-6 border dark:border-dark-copper-mid">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Campaign kopieren
              </h3>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Ab welchem Slot kopieren?
                </label>
                <input
                  type="number"
                  min="1"
                  max={totalSlots || 1}
                  value={copyFromSlot}
                  onChange={(e) => setCopyFromSlot(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                  Slots {copyFromSlot} bis {totalSlots} werden kopiert
                  ({Math.max(0, totalSlots - copyFromSlot + 1)} Slots)
                </p>
                {totalSlots > 1000 && (
                  <p className="text-xs text-orange-600 mt-2">
                    Große Campaign — das Laden der Slots kann einen Moment dauern.
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-md text-gray-700 dark:text-gray-300"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCopyCampaign}
                  disabled={copyLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {copyLoading ? 'Lade Slots...' : 'Kopieren & Neue Campaign erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiCampaignDetails;
